// @flow
import { Formik } from 'formik';
import * as React from 'react';
import { SchemaForm } from 'speed-js-react';
import { Text, Box } from 'grommet';
import styled from 'styled-components';
import memoizeOne from 'memoize-one';
import type { SchemaFormProps } from 'speed-js-react/src/components/forms/types';
import ScrollToError from './ScrollToError';


type Props = SchemaFormProps & {
    ref?: React.ElementRef<Formik>,
    actions?: (props: Object) => React.Node,
    nonFieldErrorsName: string,
    height: ?string,
    padding: ?string,
    overflow: ?string,
    enableReinitialize?: boolean,
    getGrommetFormBoxRef?: Function,
}

export const FormBox = styled(Box)`
    position: static;
    overflow: ${(props) => props.overflow || 'auto'};
    padding: ${(props) => props.padding || '12px'};
    height: ${(props) => props.height || '100%'};
`;

class GrommetForm extends React.Component<Props> {
    formRef: React.ElementRef<Box>;

    static defaultProps = {
        nonFieldErrorsName: 'non_field_errors',
        height: null,
        padding: null,
        overflow: null,
        getGrommetFormBoxRef: undefined,
    };

    constructor(props: Props) {
        super(props);

        this.formRef = React.createRef();
        this.handleSubmit = memoizeOne(this.handleSubmit);
    }

    componentDidMount(): void {
        const { onChange, getGrommetFormBoxRef } = this.props;
        const { current } = this.formRef;

        if (current && onChange) {
            current.addEventListener('change', onChange);
            if (getGrommetFormBoxRef) {
                getGrommetFormBoxRef(current);
            }
        }
    }

    componentWillUnmount(): void {
        const { onChange } = this.props;
        const { current } = this.formRef;

        if (current && onChange) {
            current.removeEventListener('change', onChange);
        }
    }

    handleSubmit = (formikHandleSubmit) => (
        (e: SyntheticEvent<HTMLFormElement>) => {
            e.stopPropagation();
            e.preventDefault();
            formikHandleSubmit(e);
        }
    )

    renderForm = (
        props: {
            handleSubmit: (e: SyntheticEvent<HTMLFormElement>) => void,
            handleReset: () => void,
            errors: { [string]: Array<string> },
            isSubmitting: boolean,
        },
        fields: { [string]: React.Node },
    ) => {
        const {
            handleSubmit, handleReset, errors, isSubmitting,
        } = props;
        const {
            actions, nonFieldErrorsName, onChange, children, padding, overflow, height,
        } = this.props;

        const nonFieldErrors: Array<string> = errors && errors[nonFieldErrorsName];

        return (
            <FormBox
                as="form"
                onSubmit={this.handleSubmit(handleSubmit)}
                onReset={handleReset}
                ref={this.formRef}
                onChange={onChange}
                gap="small"
                height={height}
                overflow={overflow}
                padding={padding}
            >
                {nonFieldErrors && (
                    <Box
                        background="status-error"
                        margin={{ vertical: 'small' }}
                        pad="small"
                        round="small"
                        id="nonFieldErrors"
                    >
                        <Text color="white">{nonFieldErrors.join('; ')}</Text>
                    </Box>
                )}
                {/* $FlowFixMe */}
                {children}
                {Object.values(fields)}
                {actions ? actions(props) : null}
                <ScrollToError isSubmitting={isSubmitting} errors={errors} />
            </FormBox>
        );
    };

    render() {
        const { actions, nonFieldErrorsName, ...props } = this.props;
        return (
            <SchemaForm {...props}>{this.renderForm}</SchemaForm>
        );
    }
}

// eslint-disable-next-line react/no-multi-comp
export default React.forwardRef<Props, React.ElementRef<Formik>>((props, ref) => (
    <GrommetForm formikRef={ref} {...props} />
));
